import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  InternalInviteCreateInput,
  InviteCreatedDto,
  OkResponse,
} from "@colab/shared";

// Mutations behind the User Manager. Each invalidates the team directory (and,
// where relevant, the roles directory the Roles → Members tab reads) so the
// table reflects the change immediately.

export function useInviteTeamMember() {
  const qc = useQueryClient();
  return useMutation<InviteCreatedDto, ApiClientError, InternalInviteCreateInput>({
    mutationFn: (input) =>
      apiFetch<InviteCreatedDto>("/admin/users/team/invite", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.team() });
      qc.invalidateQueries({ queryKey: queryKeys.settings.invites() });
    },
    onError: (err) => toast.error(err.message ?? "Failed to send invite"),
  });
}

export function useChangeUserRole() {
  const qc = useQueryClient();
  return useMutation<OkResponse, ApiClientError, { userId: string; roleId: string }>({
    mutationFn: ({ userId, roleId }) =>
      apiFetch<OkResponse>(`/admin/users/${userId}/role`, {
        method: "POST",
        body: JSON.stringify({ roleId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.team() });
      qc.invalidateQueries({ queryKey: queryKeys.users.internal() });
      qc.invalidateQueries({ queryKey: queryKeys.settings.roles() });
    },
    onError: (err) => toast.error(err.message ?? "Failed to change role"),
  });
}

export function useSetUserActive() {
  const qc = useQueryClient();
  return useMutation<OkResponse, ApiClientError, { userId: string; active: boolean }>({
    mutationFn: ({ userId, active }) =>
      apiFetch<OkResponse>(`/admin/users/${userId}/active`, {
        method: "POST",
        body: JSON.stringify({ active }),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.users.team() });
      toast.success(vars.active ? "Access restored" : "Account suspended");
    },
    onError: (err) => toast.error(err.message ?? "Failed to update access"),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation<OkResponse, ApiClientError, { userId: string }>({
    mutationFn: ({ userId }) =>
      apiFetch<OkResponse>(`/admin/users/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.team() });
      toast.success("User deleted");
    },
    onError: (err) => toast.error(err.message ?? "Failed to delete user"),
  });
}

export function useResetUserPassword() {
  return useMutation<OkResponse, ApiClientError, { userId: string }>({
    mutationFn: ({ userId }) =>
      apiFetch<OkResponse>(`/admin/users/${userId}/reset-password`, {
        method: "POST",
      }),
    onSuccess: () => toast.success("Password reset email sent"),
    onError: (err) => toast.error(err.message ?? "Failed to send reset email"),
  });
}

export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation<InviteCreatedDto, ApiClientError, { inviteId: string }>({
    mutationFn: ({ inviteId }) =>
      apiFetch<InviteCreatedDto>(`/admin/users/invites/${inviteId}/resend`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.team() });
      qc.invalidateQueries({ queryKey: queryKeys.users.partnerContacts() });
    },
    onError: (err) => toast.error(err.message ?? "Failed to resend invite"),
  });
}

export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation<OkResponse, ApiClientError, { inviteId: string }>({
    mutationFn: ({ inviteId }) =>
      apiFetch<OkResponse>(`/admin/users/invites/${inviteId}/revoke`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.users.team() });
      qc.invalidateQueries({ queryKey: queryKeys.users.partnerContacts() });
      toast.success("Invite revoked");
    },
    onError: (err) => toast.error(err.message ?? "Failed to revoke invite"),
  });
}
